import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  ArrowLeft, Upload, Camera, CreditCard, Check, X, 
  FileText, User, AlertCircle, Clock, CheckCircle
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const documentTypes = [
  { id: 'cc', name: 'Cartão de Cidadão', description: 'Documento oficial português' },
  { id: 'bi', name: 'Bilhete de Identidade', description: 'Documento de identificação' },
  { id: 'passport', name: 'Passaporte', description: 'Documento de viagem' }
];

export const CustomerVerification = () => {
  const [step, setStep] = useState(1);
  const [verificationStatus, setVerificationStatus] = useState(null);
  const [formData, setFormData] = useState({
    id_document_type: '',
    id_front: null,
    id_back: null,
    card_number: '',
    card_expiry: '',
    card_cvc: ''
  });
  const [previews, setPreviews] = useState({
    id_front: null,
    id_back: null
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const fileInputFront = useRef(null);
  const fileInputBack = useRef(null);
  const { token } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    fetchVerificationStatus();
  }, []);

  const fetchVerificationStatus = async () => {
    try {
      const response = await axios.get(`${API}/verification/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setVerificationStatus(response.data);
      
      // If already verified, redirect to dashboard
      if (response.data.is_verified) {
        navigate('/dashboard');
      }
    } catch (error) {
      console.error('Error fetching verification status:', error);
    }
  };

  const handleFileChange = async (type, event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setPreviews(prev => ({ ...prev, [type]: reader.result }));
      setFormData(prev => ({ ...prev, [type]: reader.result }));
    };
    reader.readAsDataURL(file);
  };

  const uploadDocument = async (type, base64Data) => {
    try {
      await axios.post(`${API}/verification/upload-document`, {
        document_type: type,
        document_data: base64Data
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return true;
    } catch (error) {
      console.error(`Error uploading ${type}:`, error);
      return false;
    }
  };

  const handleSubmitDocuments = async () => {
    setError('');
    setLoading(true);

    try {
      // Upload documents
      if (formData.id_front) {
        await uploadDocument('id_front', formData.id_front);
      }
      if (formData.id_back) {
        await uploadDocument('id_back', formData.id_back);
      }

      // Submit verification
      await axios.post(`${API}/verification/customer/submit`, {
        id_document_type: formData.id_document_type
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setStep(3);
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao submeter documentos');
    } finally {
      setLoading(false);
    }
  };

  const handleAddPaymentMethod = async () => {
    setError('');
    setLoading(true);

    try {
      // In production, use Stripe Elements or similar
      await axios.post(`${API}/verification/add-payment-method`, {
        card_last_four: formData.card_number.slice(-4),
        card_brand: 'visa' // Detect from card number in production
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Refresh status and redirect
      await fetchVerificationStatus();
      navigate('/dashboard');
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao adicionar método de pagamento');
    } finally {
      setLoading(false);
    }
  };

  // Show status if already submitted
  if (verificationStatus?.verification_status === 'submitted') {
    return (
      <div className="min-h-screen bg-gray-100 py-8 px-4">
        <div className="max-w-md mx-auto">
          <div className="bg-white rounded-3xl p-8 shadow-xl text-center">
            <div className="w-20 h-20 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Clock className="w-10 h-10 text-yellow-600" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Verificação em Análise</h1>
            <p className="text-gray-600 mb-6">
              Os seus documentos estão a ser analisados. Receberá uma notificação quando a verificação for concluída.
            </p>
            <Link to="/dashboard">
              <Button variant="outline" className="rounded-full">
                Voltar ao Dashboard
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (verificationStatus?.verification_status === 'rejected') {
    return (
      <div className="min-h-screen bg-gray-100 py-8 px-4">
        <div className="max-w-md mx-auto">
          <div className="bg-white rounded-3xl p-8 shadow-xl text-center">
            <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <X className="w-10 h-10 text-red-600" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Verificação Rejeitada</h1>
            <p className="text-gray-600 mb-4">
              {verificationStatus.verification_details?.rejection_reason || 'Os seus documentos não foram aprovados.'}
            </p>
            <Button 
              onClick={() => {
                setVerificationStatus(null);
                setStep(1);
              }}
              className="bg-tuggo-mint text-white rounded-full"
            >
              Tentar Novamente
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 py-8 px-4">
      <div className="max-w-md mx-auto">
        <Link to="/dashboard" className="inline-flex items-center gap-2 text-gray-600 hover:text-tuggo-mint mb-6">
          <ArrowLeft className="w-4 h-4" />
          Voltar
        </Link>

        <div className="bg-white rounded-3xl p-8 shadow-xl">
          {/* Progress */}
          <div className="flex items-center justify-center gap-4 mb-8">
            {[1, 2, 3].map((s) => (
              <div key={s} className="flex items-center">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${
                  step >= s ? 'bg-tuggo-mint text-white' : 'bg-gray-200 text-gray-500'
                }`}>
                  {step > s ? <Check className="w-5 h-5" /> : s}
                </div>
                {s < 3 && <div className={`w-8 h-1 mx-2 ${step > s ? 'bg-tuggo-mint' : 'bg-gray-200'}`} />}
              </div>
            ))}
          </div>

          {error && (
            <div className="bg-red-50 text-red-600 px-4 py-3 rounded-xl mb-6 flex items-center gap-2">
              <AlertCircle className="w-5 h-5" />
              {error}
            </div>
          )}

          {/* Step 1: Document Type */}
          {step === 1 && (
            <div>
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-tuggo-mint/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <FileText className="w-8 h-8 text-tuggo-mint" />
                </div>
                <h1 className="text-2xl font-bold text-gray-900">Verificação de Identidade</h1>
                <p className="text-gray-600">Selecione o tipo de documento</p>
              </div>

              <div className="space-y-3">
                {documentTypes.map((doc) => (
                  <button
                    key={doc.id}
                    onClick={() => setFormData({ ...formData, id_document_type: doc.id })}
                    className={`w-full p-4 rounded-xl border-2 text-left transition-all ${
                      formData.id_document_type === doc.id
                        ? 'border-tuggo-mint bg-tuggo-mint/5'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="font-semibold text-gray-900">{doc.name}</div>
                    <div className="text-sm text-gray-500">{doc.description}</div>
                  </button>
                ))}
              </div>

              <Button
                onClick={() => setStep(2)}
                disabled={!formData.id_document_type}
                className="w-full mt-6 bg-tuggo-mint text-white rounded-full py-6"
              >
                Continuar
              </Button>
            </div>
          )}

          {/* Step 2: Upload Documents */}
          {step === 2 && (
            <div>
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-tuggo-mint/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Camera className="w-8 h-8 text-tuggo-mint" />
                </div>
                <h1 className="text-2xl font-bold text-gray-900">Fotografar Documento</h1>
                <p className="text-gray-600">Tire fotos da frente e verso do seu documento</p>
              </div>

              {/* Front of Document */}
              <div className="mb-4">
                <Label className="block mb-2">Frente do Documento</Label>
                <input
                  type="file"
                  ref={fileInputFront}
                  onChange={(e) => handleFileChange('id_front', e)}
                  accept="image/*"
                  capture="environment"
                  className="hidden"
                />
                <button
                  onClick={() => fileInputFront.current?.click()}
                  className={`w-full h-40 rounded-xl border-2 border-dashed flex flex-col items-center justify-center transition-all ${
                    previews.id_front
                      ? 'border-tuggo-mint bg-tuggo-mint/5'
                      : 'border-gray-300 hover:border-tuggo-mint'
                  }`}
                >
                  {previews.id_front ? (
                    <img src={previews.id_front} alt="Frente" className="h-full w-full object-cover rounded-lg" />
                  ) : (
                    <>
                      <Upload className="w-8 h-8 text-gray-400 mb-2" />
                      <span className="text-gray-500">Clique para fotografar</span>
                    </>
                  )}
                </button>
              </div>

              {/* Back of Document */}
              <div className="mb-6">
                <Label className="block mb-2">Verso do Documento</Label>
                <input
                  type="file"
                  ref={fileInputBack}
                  onChange={(e) => handleFileChange('id_back', e)}
                  accept="image/*"
                  capture="environment"
                  className="hidden"
                />
                <button
                  onClick={() => fileInputBack.current?.click()}
                  className={`w-full h-40 rounded-xl border-2 border-dashed flex flex-col items-center justify-center transition-all ${
                    previews.id_back
                      ? 'border-tuggo-mint bg-tuggo-mint/5'
                      : 'border-gray-300 hover:border-tuggo-mint'
                  }`}
                >
                  {previews.id_back ? (
                    <img src={previews.id_back} alt="Verso" className="h-full w-full object-cover rounded-lg" />
                  ) : (
                    <>
                      <Upload className="w-8 h-8 text-gray-400 mb-2" />
                      <span className="text-gray-500">Clique para fotografar</span>
                    </>
                  )}
                </button>
              </div>

              <div className="flex gap-4">
                <Button onClick={() => setStep(1)} variant="outline" className="flex-1 rounded-full py-6">
                  Voltar
                </Button>
                <Button
                  onClick={handleSubmitDocuments}
                  disabled={!previews.id_front || !previews.id_back || loading}
                  className="flex-1 bg-tuggo-mint text-white rounded-full py-6"
                >
                  {loading ? 'A enviar...' : 'Submeter'}
                </Button>
              </div>
            </div>
          )}

          {/* Step 3: Payment Method */}
          {step === 3 && (
            <div>
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-tuggo-mint/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <CreditCard className="w-8 h-8 text-tuggo-mint" />
                </div>
                <h1 className="text-2xl font-bold text-gray-900">Método de Pagamento</h1>
                <p className="text-gray-600">Adicione um cartão para realizar pagamentos</p>
              </div>

              <div className="space-y-4">
                <div>
                  <Label>Número do Cartão</Label>
                  <Input
                    value={formData.card_number}
                    onChange={(e) => setFormData({ ...formData, card_number: e.target.value.replace(/\D/g, '').slice(0, 16) })}
                    placeholder="1234 5678 9012 3456"
                    className="mt-2"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Validade</Label>
                    <Input
                      value={formData.card_expiry}
                      onChange={(e) => setFormData({ ...formData, card_expiry: e.target.value })}
                      placeholder="MM/AA"
                      className="mt-2"
                    />
                  </div>
                  <div>
                    <Label>CVC</Label>
                    <Input
                      value={formData.card_cvc}
                      onChange={(e) => setFormData({ ...formData, card_cvc: e.target.value.replace(/\D/g, '').slice(0, 4) })}
                      placeholder="123"
                      type="password"
                      className="mt-2"
                    />
                  </div>
                </div>
              </div>

              <div className="bg-yellow-50 rounded-xl p-4 mt-6 flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-yellow-600 mt-0.5" />
                <div className="text-sm text-yellow-700">
                  Os dados do cartão são processados de forma segura. Não armazenamos os dados completos do seu cartão.
                </div>
              </div>

              <Button
                onClick={handleAddPaymentMethod}
                disabled={!formData.card_number || !formData.card_expiry || !formData.card_cvc || loading}
                className="w-full mt-6 bg-tuggo-mint text-white rounded-full py-6"
              >
                {loading ? 'A processar...' : 'Adicionar Cartão'}
              </Button>

              <button
                onClick={() => navigate('/dashboard')}
                className="w-full mt-3 text-gray-500 hover:text-gray-700 text-sm"
              >
                Adicionar mais tarde
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
